/**
 * Componente React compartilhado que renderiza uma proposta comercial
 * KM Gestão no formato visual final. Usado em duas superfícies:
 *
 *   - **Prévia no Superadmin** (`ProposalPreviewModal`): Karla revisa
 *     antes de enviar — mode="preview" (sem botões de ação do lead)
 *   - **Rota pública** `/proposta/:token` (PR-M1-01c-ii.b): o lead
 *     consome — mode="public" (expõe botões Aceitar + Tenho uma dúvida)
 *
 * **Layout refinado em PR-M1-01c-ii.e-layout (2026-04-24)**:
 *   Antes usávamos um `<article max-w-3xl>` único. Em telas largas
 *   sobrava espaço vazio nas laterais (reportado pelo Alexandre).
 *   Agora estruturamos em **seções full-width com bandas alternadas
 *   (bg-background ↔ bg-muted/20)** e conteúdo interno constrained
 *   em `max-w-4xl` (reading comfort preservado). No combo, cards SaaS
 *   e Consultoria vão lado-a-lado em `lg+` (aproveita a largura).
 *
 *   Essa estrutura funciona **idêntica no modal** — o DialogContent já
 *   limita a largura externa, então os `w-full` batem no limite do modal.
 *   E na rota pública, `w-full` = viewport inteira.
 *
 * **Padrão visual**:
 *   - Logomarca oficial SVG (via `<Brand />`)
 *   - Hero full-width com gradient accent sutil
 *   - Seções numeradas com destaque visual (accent gold pontual)
 *   - Cards SaaS + Consultoria side-by-side em combo (lg+)
 *   - Timeline de cobrança em band própria quando combo
 *   - Bottom bar sticky com ações (mode=public)
 *
 * Respeita tokens DS — funciona em light+dark, mobile-first.
 * Baseado no padrão estrutural de
 * `docs/Proposta-Comercial-Dra-Danielle-Fabretti.md`.
 */
import { useState, type ReactNode } from 'react';

import { Brand } from '../../components/Brand.js';

import { CONSULTING_MODALITY_LABELS, PLAN_LABELS, type LeadProposalSummary } from './schemas.js';
import { NotesDiffView } from './NotesDiffView.js';
import type { PlatformSettings } from './platform-settings.js';
import { replaceTokens } from './token-replace.js';

export interface ProposalPublicViewProps {
  proposal: LeadProposalSummary;
  platformSettings: PlatformSettings;
  lead: { full_name: string; clinic_name_guess: string | null };
  mode: 'preview' | 'public';
  onAccept?: () => void;
  onQuestion?: () => void;
  isAcceptPending?: boolean;
  isQuestionPending?: boolean;
  /** Callback pra salvar o texto do "Escopo desta proposta" (bloco 03)
   * via edição inline. Quando presente + `mode=preview`, botão "✎ Editar"
   * aparece no bloco. Implementado em PR-M1-01c-ii.e (2026-04-24) a pedido
   * do Alexandre — "a pessoa clica na palavra editar e abre para ela editar". */
  onSaveNotes?: ((notes: string) => Promise<void> | void) | undefined;
  /** Loading state pro botão Salvar da edição inline. */
  isSavingNotes?: boolean | undefined;
}

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** Detecta títulos honoríficos pra pular ao extrair o primeiro nome.
 * Antes fazíamos `full_name.split(' ')[0]` e o lead "Dr. Danielle" virava
 * "Dr." (reportado pelo Alexandre 2026-04-24). */
const HONORIFIC_REGEX = /^(Dr|Dra|Sr|Sra|Eng|Prof)\.?$/i;

function extractFirstName(fullName: string): string {
  const tokens = fullName.trim().split(/\s+/).filter(Boolean);
  const meaningful = tokens.find((t) => !HONORIFIC_REGEX.test(t));
  return meaningful ?? tokens[0] ?? fullName;
}

/** Remove travessões (em-dash/meia-risca) do texto da IA. Alexandre
 * pediu em 2026-04-24 que a proposta não tenha travessões. Já reforcei
 * no prompt da IA, mas textos já gerados ficam com os travessões —
 * este sanitizer limpa na hora da renderização pra não precisar
 * regenerar. Padrão de substituição:
 *   - " — " / " – " → ". " (quebra clara entre frases)
 *   - "—"/"–" isolado → ", " (pausa curta)
 *
 * Limitação aceita: se o texto for "R$ 5–R$ 10" vira "R$ 5, R$ 10" —
 * contexto de negociação não deveria ter esse range, então OK. */
function sanitizeProposalText(text: string): string {
  return text.replace(/\s+[—–]\s+/g, '. ').replace(/[—–]/g, ', ');
}

/** Wrapper de seção full-width com banda de cor configurável e conteúdo
 * interno constrained. Usado pra cada bloco numerado + footer.
 *
 * Modernização visual 2026-04-24 (pedido do Alexandre): em vez de
 * cores chapadas (`bg-background`, `bg-muted/20`), cada banda combina
 * um gradient linear base + um radial accent muito sutil num canto.
 * Mantém a MESMA paleta (accent dourado + tokens do DS) mas dá
 * profundidade/textura que faz a proposta parecer menos "folha de
 * Word" e mais "material impresso premium". Intensidade baixa
 * (/3–/8 de opacidade) pra não competir com o conteúdo. */
function Band({
  tone,
  children,
  innerClassName,
}: {
  tone: 'base' | 'soft' | 'footer';
  children: ReactNode;
  /** Override pra casos que precisam de `max-w-6xl` (ex: grid 2-col combo). */
  innerClassName?: string;
}) {
  // Cada tone define até 3 camadas visuais:
  //   1. `base` — linear gradient sutil como cor de fundo
  //   2. `before:` — linha degradê accent no topo da seção (separador
  //      decorativo entre bandas, pedido do Alexandre 2026-04-24:
  //      "cada sessão com cor sutil degrade separando")
  //   3. `after:` — blob radial accent num canto (dá "vida" ao fundo
  //      sem poluir, intensidade /6–/8)
  // Direção alternada entre base/soft pra não ficar repetitivo quando
  // duas seções do mesmo tone se empilham por acaso.
  //
  // A linha separadora fica no TOPO (before:top-0) de todas as bandas —
  // como o hero cinematográfico vem antes, ela também separa a transição
  // navy → card, dando continuidade visual pro dourado accent.
  const separatorClasses =
    'before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-px ' +
    'before:bg-gradient-to-r before:from-transparent before:via-accent/40 before:to-transparent ' +
    'before:content-[""]';

  const toneClasses = {
    base:
      'bg-gradient-to-br from-background via-background to-accent/[0.03] ' +
      'after:pointer-events-none after:absolute after:right-0 after:top-0 after:size-[32rem] ' +
      'after:bg-[radial-gradient(circle_at_top_right,theme(colors.accent/0.08),transparent_70%)] ' +
      'after:content-[""] ' +
      separatorClasses,
    soft:
      'bg-gradient-to-bl from-muted/30 via-muted/10 to-background ' +
      'after:pointer-events-none after:absolute after:-left-20 after:bottom-0 after:size-[28rem] ' +
      'after:bg-[radial-gradient(circle_at_bottom_left,theme(colors.accent/0.06),transparent_70%)] ' +
      'after:content-[""] ' +
      separatorClasses,
    footer: 'bg-gradient-to-b from-muted/40 to-muted/20 ' + separatorClasses,
  }[tone];
  return (
    <section className={`relative w-full overflow-hidden ${toneClasses}`}>
      <div
        className={`relative z-10 ${innerClassName ?? 'mx-auto w-full max-w-4xl px-4 py-12 sm:px-8 sm:py-16'}`}
      >
        {children}
      </div>
    </section>
  );
}

/** Bloco numerado com título destacado. Padroniza o ritmo visual. */
function NumberedBlock({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-4">
      <header className="flex items-center gap-4">
        <span
          aria-hidden="true"
          className="flex size-10 shrink-0 items-center justify-center rounded-full border border-accent/40 bg-accent/10 text-sm font-bold text-accent"
        >
          {number}
        </span>
        <div className="flex-1">
          <h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
            {title}
          </h2>
        </div>
      </header>
      <div className="ml-0 text-base leading-relaxed text-foreground sm:ml-14">{children}</div>
    </div>
  );
}

/** Linha "label · valor". Stack vertical até `md+` (768px), lado-a-lado
 * acima disso. Dentro do grid 2-col (combo em `xl+`), cada coluna tem
 * ~500-600px — label fixo em 140px deixa espaço confortável pro valor.
 *
 * `changed=true` aplica highlight accent (PR-M1-01c-ii.g) — usado quando
 * o lead reabre a proposta após Karla reajustar em resposta à dúvida.
 * `previousDisplay` mostra o valor anterior (do snapshot) riscado + tooltip. */
function FactRow({
  label,
  children,
  changed = false,
  previousDisplay,
}: {
  label: string;
  children: ReactNode;
  changed?: boolean;
  previousDisplay?: string | null;
}) {
  return (
    <div
      className={
        changed
          ? 'relative -mx-3 flex flex-col gap-1 rounded-md border-l-4 border-accent bg-accent/10 p-3 last:border-b-0 md:flex-row md:items-baseline md:gap-4'
          : 'flex flex-col gap-1 border-b border-border py-3 last:border-b-0 md:flex-row md:items-baseline md:gap-4'
      }
    >
      <dt className="text-sm font-medium uppercase tracking-wider text-muted-foreground md:min-w-[140px]">
        {label}
        {changed ? (
          <span
            className="ml-2 inline-flex items-center rounded-full bg-accent/20 px-2 py-0.5 text-xs font-bold tracking-normal text-accent"
            title="Ajustado após sua dúvida"
          >
            ✨ ajustado
          </span>
        ) : null}
      </dt>
      <dd className="text-base font-medium text-foreground">
        {children}
        {changed && previousDisplay ? (
          <span className="ml-2 text-sm font-normal text-muted-foreground line-through">
            {previousDisplay}
          </span>
        ) : null}
      </dd>
    </div>
  );
}

export function ProposalPublicView({
  proposal,
  platformSettings,
  lead,
  mode,
  onAccept,
  onQuestion,
  isAcceptPending = false,
  isQuestionPending = false,
  onSaveNotes,
  isSavingNotes = false,
}: ProposalPublicViewProps) {
  // Estado da edição inline de notas (só usado em mode=preview + quando
  // onSaveNotes está presente). Ver bloco 03 "Escopo desta proposta".
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState<string>(proposal.notes ?? '');
  const canEditNotes = mode === 'preview' && typeof onSaveNotes === 'function';

  const handleStartEdit = () => {
    setNotesDraft(proposal.notes ?? '');
    setIsEditingNotes(true);
  };
  const handleCancelEdit = () => setIsEditingNotes(false);
  const handleSaveEdit = async () => {
    if (!onSaveNotes) return;
    await onSaveNotes(notesDraft.trim());
    setIsEditingNotes(false);
  };
  const { contratada, proposal_footer, proposal_copy } = platformSettings;

  const hasSaas = proposal.proposal_type === 'saas' || proposal.proposal_type === 'saas_consulting';
  const hasConsulting =
    proposal.proposal_type === 'consulting' || proposal.proposal_type === 'saas_consulting';
  const isCombo = proposal.proposal_type === 'saas_consulting';

  const destinatario = lead.clinic_name_guess ?? lead.full_name;
  const firstName = extractFirstName(lead.full_name);

  // Tokens disponíveis para templates de `proposal_copy` (migration 0053
  // + PR-M1-01c-ii.j). Karla pode editar o template via Superadmin e
  // estes valores substituem os placeholders `{chave}` na hora do render.
  const copyTokens = {
    firstName,
    destinatario,
    razao_social: contratada.razao_social,
    nome_fantasia: contratada.nome_fantasia,
    responsavel_nome: contratada.responsavel_nome,
  };

  // Parse dos campos que são listas (string com separador) pro array
  // que o map vai iterar. `about_us_paragraphs` usa `\n\n` (entre
  // parágrafos); `considerations_bullets` usa `\n` (entre bullets).
  // `.filter(...length > 0)` descarta linhas em branco caso a Karla
  // deixe quebras extras no editor.
  const aboutUsParagraphs = proposal_copy.about_us_paragraphs
    ? proposal_copy.about_us_paragraphs.split('\n\n').filter((p) => p.trim().length > 0)
    : [];
  const considerationsBullets = proposal_copy.considerations_bullets
    ? proposal_copy.considerations_bullets.split('\n').filter((p) => p.trim().length > 0)
    : [];
  const createdAtFormatted = new Date(proposal.created_at).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  const validUntilFormatted = new Date(proposal.valid_until).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  // Número do bloco "Considerações" depende de quais seções anteriores
  // estão presentes (notas + SaaS + Consulting + Timeline). Mantém
  // numeração sequencial sem buracos.
  const considerationsNumber = (() => {
    let n = 2; // 01 Olá, 02 Sobre nós
    if (proposal.notes) n++;
    if (hasSaas || hasConsulting) n++;
    if (isCombo) n++;
    return String(n + 1).padStart(2, '0');
  })();

  // **Diff highlighting** (PR-M1-01c-ii.g): se houver snapshot do momento
  // em que o lead enviou dúvida, destacamos as FactRows que mudaram.
  // Snapshot é nullable: se for null, a proposta ainda não passou por
  // ciclo de dúvida → sem highlight.
  const snapshot = proposal.pre_negotiation_snapshot;
  /** Compara o valor atual com o do snapshot. Retorna true se snapshot
   * existe e os valores diferem meaningfully (strings/numbers/booleans). */
  const didChange = (field: keyof NonNullable<typeof snapshot>): boolean => {
    if (!snapshot) return false;
    const before = snapshot[field];
    const after = proposal[field as keyof typeof proposal];
    if (before === null && after === null) return false;
    if (before === null || after === null) return true;
    return before !== after;
  };
  const formatSnapshotValue = (field: keyof NonNullable<typeof snapshot>): string | null => {
    if (!snapshot) return null;
    const v = snapshot[field];
    if (v === null || v === undefined) return null;
    if (typeof v === 'number') {
      // Campos monetários
      if (
        field === 'custom_price_brl' ||
        field === 'consulting_installment_brl' ||
        field === 'consulting_extra_hour_brl'
      ) {
        return BRL.format(v);
      }
      // Campos de porcentagem
      if (field === 'discount_percent') return `${v}%`;
      // Outros campos numéricos (dias, meses, parcelas, horas)
      return String(v);
    }
    if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
    if (typeof v === 'string') return v;
    return null;
  };

  // Outer wrapper: no **public mode** usamos o navy do brand
  // (`bg-sidebar`) preenchendo as laterais de telas largas com o
  // documento da proposta como frame centralizado e shadow.
  // No **preview mode** (modal), mantemos fundo neutro — o modal já
  // tem seu próprio container/shadow; sem navy nas "laterais" porque
  // não existem laterais (o DialogContent tem largura fixa).
  const outerClasses =
    mode === 'public' ? 'w-full bg-sidebar text-foreground' : 'w-full bg-background';
  const frameClasses =
    mode === 'public'
      ? 'mx-auto w-full max-w-6xl bg-background shadow-2xl sm:my-6 sm:rounded-xl sm:overflow-hidden'
      : 'w-full bg-background';

  return (
    <div className={outerClasses}>
      <div className={frameClasses}>
        {/* HERO — cinematográfico, paridade visual com /login.
            Imagem de recepção + overlay navy + card glass. Proposta é
            cartão de visita: transmite padrão da plataforma antes mesmo
            do lead ler o conteúdo (PR pedido pelo Alexandre 2026-04-24).

            Implementação espelha o `AuthShell`:
              1. camada de imagem full-bleed bg-cover bg-center
              2. camada de overlay gradient navy (primary/65 → 50 → 70)
              3. conteúdo em `relative` por cima — cores claras pra
                 contraste com overlay escuro */}
        <section className="relative w-full overflow-hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: 'url(/fundo-paginas-login-logout.png)' }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/65 via-primary/50 to-primary/70"
          />
          <div className="relative mx-auto w-full max-w-4xl px-4 py-16 sm:px-8 sm:py-24">
            <header className="space-y-8 text-center">
              <div className="flex justify-center">
                {/* `onDark` força a variante clara do logo pra ser legível
                    sobre o overlay navy — mesmo padrão do login. */}
                <Brand onDark className="h-16 w-auto sm:h-20" />
              </div>
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-accent">
                  Proposta comercial exclusiva
                </p>
                <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
                  {destinatario}
                </h1>
                <p className="text-lg text-white/80">
                  Preparada com cuidado por {contratada.responsavel_nome}
                </p>
              </div>
              {/* Glass card (bg branco translúcido com blur) — mesmo
                  efeito do card de login, adaptado pro badge de validade. */}
              <div className="inline-flex flex-col items-center gap-1 rounded-xl border border-white/20 bg-white/10 px-6 py-3 shadow-xl backdrop-blur-md">
                <p className="text-xs font-medium uppercase tracking-wider text-white/70">
                  Validade
                </p>
                <p className="text-base font-semibold text-white">até {validUntilFormatted}</p>
              </div>
              <p className="text-sm text-white/70">Emitida em {createdAtFormatted}</p>
            </header>
          </div>
        </section>

        {/* 01 — Hero (título + intro configuráveis em
            platform_settings: proposal.hero_title + proposal.hero_intro_template).
            Ambos campos suportam tokens {firstName} e {destinatario} — o bug
            de 2026-04-24 (Alexandre reportou: "{firstName}" literal aparecia
            só no título do Bloco 01) era porque só `hero_intro_template`
            passava por replaceTokens. Agora os 2 passam. */}
        <Band tone="base">
          <NumberedBlock
            number="01"
            title={replaceTokens(proposal_copy.hero_title || 'Olá, é um prazer', copyTokens)}
          >
            <p className="whitespace-pre-line text-lg">
              {replaceTokens(proposal_copy.hero_intro_template, copyTokens)}
            </p>
          </NumberedBlock>
        </Band>

        {/* 02 — Sobre nós (configurável em platform_settings:
            proposal.about_us_title + proposal.about_us_paragraphs).
            Título também passa por replaceTokens pra consistência — se o
            user quiser colocar {nome_fantasia} no título, funciona.
            Parágrafos separados por \n\n no banco; aqui cada um vira um <p>. */}
        {aboutUsParagraphs.length > 0 ? (
          <Band tone="soft">
            <NumberedBlock
              number="02"
              title={replaceTokens(proposal_copy.about_us_title || 'Sobre nós', copyTokens)}
            >
              {aboutUsParagraphs.map((paragraph, idx) => (
                <p key={idx} className={idx === 0 ? '' : 'mt-3'}>
                  {replaceTokens(paragraph, copyTokens)}
                </p>
              ))}
            </NumberedBlock>
          </Band>
        ) : null}

        {/* 03 — Escopo desta proposta (só se tiver notas OU se estiver
            no modo edição). Highlight accent quando o texto mudou após
            a dúvida. Botão "✎ Editar" aparece em mode=preview pra Karla
            refinar o texto sem sair da prévia (PR-M1-01c-ii.e). */}
        {proposal.notes || (canEditNotes && isEditingNotes) ? (
          <Band tone="base">
            <NumberedBlock number="03" title="Escopo desta proposta">
              {didChange('notes') && !isEditingNotes ? (
                <div className="mb-3 inline-flex items-center gap-2 rounded-md border border-accent/40 bg-accent/10 px-3 py-1.5 text-xs font-semibold text-accent">
                  ✨ Texto do escopo foi ajustado após sua dúvida
                </div>
              ) : null}

              {isEditingNotes ? (
                // Modo edição — textarea + Salvar/Cancelar.
                <div className="space-y-3">
                  <textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={12}
                    maxLength={4_000}
                    disabled={isSavingNotes}
                    className="w-full rounded-md border border-input bg-background px-4 py-3 text-base leading-relaxed text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Escreva o escopo detalhado da proposta aqui…"
                  />
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground">
                      {notesDraft.trim().length} / 4.000 caracteres
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        disabled={isSavingNotes}
                        className="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveEdit}
                        disabled={isSavingNotes || notesDraft.trim().length < 10}
                        aria-busy={isSavingNotes || undefined}
                        className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isSavingNotes ? 'Salvando…' : 'Salvar'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Modo leitura — texto formatado + botão Editar (se permitido).
                <>
                  {canEditNotes ? (
                    <div className="mb-3 flex justify-end">
                      <button
                        type="button"
                        onClick={handleStartEdit}
                        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <span aria-hidden="true">✎</span> Editar escopo
                      </button>
                    </div>
                  ) : null}
                  <div
                    className={
                      didChange('notes')
                        ? 'rounded-lg border-l-4 border-accent bg-accent/5 p-5 ring-1 ring-accent/30'
                        : 'rounded-lg border-l-4 border-accent bg-muted/20 p-5'
                    }
                  >
                    <div className="whitespace-pre-wrap text-foreground">
                      {proposal.notes ? (
                        // Quando houve edição pós-negociação (didChange('notes')
                        // e o snapshot tem o texto anterior), renderizamos com
                        // highlight âmbar nos trechos adicionados. Senão cai
                        // no texto cru sanitizado, igual antes. `before/after`
                        // passam pelo sanitize antes do diff — previne falsos
                        // positivos de travessões convertidos em vírgula.
                        didChange('notes') && snapshot?.notes ? (
                          <NotesDiffView
                            before={sanitizeProposalText(snapshot.notes)}
                            after={sanitizeProposalText(proposal.notes)}
                          />
                        ) : (
                          sanitizeProposalText(proposal.notes)
                        )
                      ) : (
                        <span className="italic text-muted-foreground">
                          Sem texto ainda. Clique em <strong>Editar escopo</strong> pra escrever.
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </NumberedBlock>
          </Band>
        ) : null}

        {/* 04 (+05) — Consultoria primeiro, depois SaaS (PR-M1-01c-ii.i,
            2026-04-24): Alexandre pediu inverter a ordem porque Consultoria
            é o core do negócio da Karla; SaaS é o bônus.
            Grid 2-col em lg+ no combo. Banda `soft` pra fundo cinza e
            cards internos destacarem. Largura interna 6xl no combo. */}
        {hasSaas || hasConsulting ? (
          <Band
            tone="soft"
            innerClassName={
              isCombo
                ? 'mx-auto w-full max-w-6xl px-4 py-12 sm:px-8 sm:py-16'
                : 'mx-auto w-full max-w-4xl px-4 py-12 sm:px-8 sm:py-16'
            }
          >
            <div className={isCombo ? 'grid gap-8 lg:grid-cols-2' : ''}>
              {hasConsulting ? (
                <NumberedBlock
                  number={proposal.notes ? '04' : '03'}
                  title="Consultoria Karla Martins"
                >
                  <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
                    <dl className="space-y-0">
                      <FactRow
                        label="Investimento"
                        changed={
                          didChange('consulting_installments_count') ||
                          didChange('consulting_installment_brl')
                        }
                        previousDisplay={
                          snapshot
                            ? `${snapshot.consulting_installments_count ?? '?'}× ${
                                snapshot.consulting_installment_brl !== null
                                  ? BRL.format(snapshot.consulting_installment_brl)
                                  : 'não informado'
                              }`
                            : null
                        }
                      >
                        <span className="text-xl font-bold text-foreground">
                          {proposal.consulting_installments_count ?? '?'}×{' '}
                          {proposal.consulting_installment_brl !== null
                            ? BRL.format(proposal.consulting_installment_brl)
                            : 'não informado'}
                        </span>
                      </FactRow>
                      <FactRow
                        label="Duração"
                        changed={didChange('consulting_duration_months')}
                        previousDisplay={
                          didChange('consulting_duration_months')
                            ? `${formatSnapshotValue('consulting_duration_months')} meses`
                            : null
                        }
                      >
                        {proposal.consulting_duration_months ?? '?'} meses de acompanhamento
                      </FactRow>
                      <FactRow
                        label="Modalidade"
                        changed={didChange('consulting_modality')}
                        previousDisplay={
                          snapshot?.consulting_modality
                            ? CONSULTING_MODALITY_LABELS[snapshot.consulting_modality]
                            : null
                        }
                      >
                        {proposal.consulting_modality
                          ? CONSULTING_MODALITY_LABELS[proposal.consulting_modality]
                          : 'a definir'}
                      </FactRow>
                      <FactRow
                        label="Horas semanais"
                        changed={didChange('consulting_weekly_hours')}
                        previousDisplay={
                          didChange('consulting_weekly_hours')
                            ? `${formatSnapshotValue('consulting_weekly_hours')}h`
                            : null
                        }
                      >
                        {proposal.consulting_weekly_hours ?? '?'}h por semana, dedicadas a reuniões,
                        análises e preparação de materiais
                      </FactRow>
                      {proposal.consulting_extra_hour_brl !== null ? (
                        <FactRow
                          label="Horas adicionais"
                          changed={didChange('consulting_extra_hour_brl')}
                          previousDisplay={formatSnapshotValue('consulting_extra_hour_brl')}
                        >
                          {BRL.format(proposal.consulting_extra_hour_brl)} por hora (quando
                          solicitado)
                        </FactRow>
                      ) : null}
                    </dl>
                  </div>
                </NumberedBlock>
              ) : null}

              {hasSaas ? (
                <NumberedBlock
                  number={
                    hasConsulting ? (proposal.notes ? '05' : '04') : proposal.notes ? '04' : '03'
                  }
                  title="Plataforma KM Gestão"
                >
                  <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
                    <dl className="space-y-0">
                      <FactRow label="Plano">
                        {proposal.plan_name_pt_br ??
                          (proposal.plan_key
                            ? (PLAN_LABELS[proposal.plan_key] ?? proposal.plan_key)
                            : 'a definir')}
                      </FactRow>
                      {proposal.trial_days !== null && proposal.trial_days > 0 ? (
                        <FactRow
                          label="Trial inicial"
                          changed={didChange('trial_days')}
                          previousDisplay={
                            didChange('trial_days')
                              ? `${formatSnapshotValue('trial_days')} dias`
                              : null
                          }
                        >
                          {proposal.trial_days} dias sem cobrança
                        </FactRow>
                      ) : null}
                      {proposal.custom_price_brl !== null ? (
                        <FactRow
                          label="Preço mensal"
                          changed={didChange('custom_price_brl')}
                          previousDisplay={formatSnapshotValue('custom_price_brl')}
                        >
                          {BRL.format(proposal.custom_price_brl)}{' '}
                          <span className="text-sm font-normal text-muted-foreground">
                            (preço personalizado)
                          </span>
                        </FactRow>
                      ) : proposal.discount_percent !== null && proposal.discount_percent > 0 ? (
                        <FactRow
                          label="Condição especial"
                          changed={didChange('discount_percent')}
                          previousDisplay={formatSnapshotValue('discount_percent')}
                        >
                          {proposal.discount_percent}% de desconto sobre o plano base
                        </FactRow>
                      ) : null}
                    </dl>
                  </div>
                </NumberedBlock>
              ) : null}
            </div>
          </Band>
        ) : null}

        {/* Timeline de cobrança (só combo) — banda própria pra ficar
          visualmente claro entre os cards e as considerações */}
        {isCombo ? (
          <Band tone="base">
            <div className="space-y-4">
              <header className="flex items-center gap-4">
                <span
                  aria-hidden="true"
                  className="flex size-10 shrink-0 items-center justify-center rounded-full border border-accent/40 bg-accent/10 text-sm font-bold text-accent"
                >
                  ⟶
                </span>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                    Cronograma de cobrança
                  </h2>
                </div>
              </header>
              <div className="ml-0 overflow-hidden rounded-lg border border-accent/30 sm:ml-14">
                <div className="grid divide-y divide-border sm:grid-cols-2 sm:divide-x sm:divide-y-0">
                  <div className="space-y-2 p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Enquanto a consultoria estiver vigente
                    </p>
                    <ul className="space-y-1.5 text-sm">
                      <li className="flex items-start gap-2">
                        <span aria-hidden="true" className="mt-1 text-accent">
                          ●
                        </span>
                        <span>
                          <strong className="text-foreground">Consultoria</strong>: cobrança mensal
                          via Asaas (início imediato)
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span aria-hidden="true" className="mt-1 text-accent">
                          ●
                        </span>
                        <span>
                          <strong className="text-foreground">Plataforma SaaS</strong>:{' '}
                          {proposal.saas_free_during_consulting ? (
                            <span className="inline-flex items-center rounded-full bg-accent/20 px-2 py-0.5 text-xs font-semibold text-accent">
                              Gratuita (bônus)
                            </span>
                          ) : (
                            'cobrança mensal via Asaas desde o início'
                          )}
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="space-y-2 bg-muted/30 p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Após o término da consultoria
                    </p>
                    <ul className="space-y-1.5 text-sm">
                      <li className="flex items-start gap-2">
                        <span aria-hidden="true" className="mt-1 text-muted-foreground">
                          ●
                        </span>
                        <span className="text-muted-foreground">
                          <strong className="text-foreground">Consultoria</strong>: contrato
                          encerrado, renovação consultada
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span aria-hidden="true" className="mt-1 text-accent">
                          ●
                        </span>
                        <span>
                          <strong className="text-foreground">Plataforma SaaS</strong>:{' '}
                          {proposal.saas_free_during_consulting
                            ? 'cobrança mensal via Asaas (mediante confirmação do cliente)'
                            : 'cobrança mensal via Asaas (sem alteração)'}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Band>
        ) : null}

        {/* Considerações — lista híbrida:
            - Bullet dinâmico: validade (computado da proposta)
            - Bullets estáticos: vêm de platform_settings.proposal.considerations_bullets
              (Karla edita pela UI do Superadmin)
            - Bullet condicional dinâmico: continuidade pós-consultoria
            - Bullet footer institucional: proposal_footer.institutional_text
            A ordem dos dinâmicos em volta dos estáticos preserva a
            narrativa original (validade primeiro, fechamento por último). */}
        <Band tone="soft">
          <NumberedBlock number={considerationsNumber} title="Considerações">
            <ul className="space-y-3">
              {[
                `Esta proposta tem validade até ${validUntilFormatted}.`,
                ...considerationsBullets,
                // Bullet condicional pós-consultoria (só aparece quando
                // proposal_type inclui consultoria). Template editável
                // em platform_settings.proposal.post_consulting_terms_template
                // (migration 0054). Tokens:
                //   {consulting_duration_months} → número de meses.
                //   {saas_cobranca_note} → trecho sobre cobrança do SaaS,
                //      preenchido só em combo (em consulting puro fica vazio).
                ...(hasConsulting && proposal_copy.post_consulting_terms_template
                  ? [
                      replaceTokens(proposal_copy.post_consulting_terms_template, {
                        consulting_duration_months: String(
                          proposal.consulting_duration_months ?? '?',
                        ),
                        saas_cobranca_note: isCombo
                          ? ' (cobrança passa a ser regular após confirmação ativa)'
                          : '',
                      }),
                    ]
                  : []),
                proposal_footer.institutional_text ||
                  'A proponente se compromete a guardar em sigilo todas as informações levantadas, reservadas a qualquer tempo ao contratante.',
              ].map((text, idx) => (
                <li key={idx} className="flex gap-3">
                  <span
                    aria-hidden="true"
                    className="mt-2 size-1.5 shrink-0 rounded-full bg-accent"
                  />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </NumberedBlock>
        </Band>

        {/* FOOTER — assinatura full-width */}
        <Band tone="footer">
          <footer className="space-y-2 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-muted-foreground">
              Atenciosamente
            </p>
            <p className="text-xl font-bold text-foreground">{contratada.responsavel_nome}</p>
            <p className="text-sm text-foreground">{contratada.responsavel_cargo}</p>
            <div className="mt-6 inline-flex flex-col gap-0.5 text-xs text-muted-foreground sm:flex-row sm:items-center sm:gap-3">
              <span className="font-medium text-foreground">{contratada.razao_social}</span>
              <span aria-hidden="true" className="hidden sm:inline">
                ·
              </span>
              <span>CNPJ {contratada.cnpj}</span>
              <span aria-hidden="true" className="hidden sm:inline">
                ·
              </span>
              <span>{contratada.cidade_uf}</span>
              <span aria-hidden="true" className="hidden sm:inline">
                ·
              </span>
              <a
                href={`mailto:${contratada.email_contato}`}
                className="text-accent hover:underline"
              >
                {contratada.email_contato}
              </a>
            </div>
          </footer>
        </Band>
      </div>

      {/* Sticky bottom bar (mode=public apenas) — fica FORA do frame
          overflow-hidden pra sticky funcionar corretamente em relação
          à viewport. Hierarquia visual: "Dúvida" é secundário (outline
          translúcido, baixo contraste); "Aceitar" é o CTA — gold saturado,
          maior, com glow + ring pra puxar o olho e aumentar conversão.
          Decisão UX validada com Alexandre em 2026-04-24. */}
      {mode === 'public' ? (
        <div className="sticky bottom-0 z-10 border-t border-sidebar-border bg-sidebar/90 backdrop-blur-md shadow-lg">
          <div className="mx-auto flex max-w-4xl flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-end sm:px-8 sm:py-5">
            <button
              type="button"
              onClick={onQuestion}
              disabled={isQuestionPending || isAcceptPending}
              className="inline-flex h-11 items-center justify-center rounded-md border border-sidebar-border/60 bg-transparent px-5 text-sm font-medium text-sidebar-foreground/80 transition-colors hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isQuestionPending ? 'Enviando…' : 'Tenho uma dúvida antes de aceitar'}
            </button>
            <button
              type="button"
              onClick={onAccept}
              disabled={isAcceptPending || isQuestionPending}
              className="group relative inline-flex h-14 items-center justify-center rounded-md bg-accent px-8 text-base font-bold text-accent-foreground shadow-[0_8px_24px_-4px_rgb(197_167_112_/_0.5)] ring-2 ring-accent/40 ring-offset-2 ring-offset-sidebar transition-all hover:bg-accent/95 hover:shadow-[0_12px_32px_-4px_rgb(197_167_112_/_0.65)] hover:ring-accent/60 focus-visible:outline-none focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none"
            >
              <span className="flex items-center gap-2">
                {isAcceptPending ? 'Processando…' : 'Aceitar e avançar'}
                {!isAcceptPending ? (
                  <span
                    aria-hidden="true"
                    className="inline-block transition-transform group-hover:translate-x-1"
                  >
                    →
                  </span>
                ) : null}
              </span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
